import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Divider
} from '@mui/material';
import {
  Email,
  CheckCircle,
  Error as ErrorIcon,
  Refresh,
  Send
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const PROPERTY_CHECK_POST_VERIFY_REDIRECT_KEY = 'eqaya_property_check_post_verify_redirect';

/**
 * EmailVerificationFlow Component
 * 
 * Handles all email verification workflows:
 * - Email verification after registration
 * - Resending verification emails
 * - Email verification status display
 */
const EmailVerificationFlow = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, updateUser } = useAuth();
  
  const [verificationState, setVerificationState] = useState('checking'); // checking, verified, error, needs_verification
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [newEmail, setNewEmail] = useState('');
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [userId, setUserId] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [codeExpirySeconds, setCodeExpirySeconds] = useState(0);
  const digitRefs = useRef([]);

  // Get parameters from URL
  const urlUserId = searchParams.get('userId');
  const urlCode = searchParams.get('code');
  const success = searchParams.get('success');
  const errorParam = searchParams.get('error');
  const messageParam = searchParams.get('message');
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const getRedirectTarget = () => {
    const from = location.state?.from;
    if (typeof from === 'string') return from;
    if (from?.pathname) return `${from.pathname}${from.search || ''}`;
    const storedPropertyCheckTarget =
      sessionStorage.getItem(PROPERTY_CHECK_POST_VERIFY_REDIRECT_KEY) ||
      localStorage.getItem(PROPERTY_CHECK_POST_VERIFY_REDIRECT_KEY);
    if (storedPropertyCheckTarget) {
      try {
        const parsed = JSON.parse(storedPropertyCheckTarget);
        if (typeof parsed === 'string') return parsed;
        if (parsed?.pathname) return `${parsed.pathname}${parsed.search || ''}${parsed.hash || ''}`;
      } catch (_) {
        return storedPropertyCheckTarget;
      }
    }
    return '/dashboard';
  };

  useEffect(() => {
    // Set userId from URL or localStorage
    if (urlUserId) {
      setUserId(urlUserId);
    } else if (user?.id) {
      setUserId(user.id);
    } else {
      // Try to get from localStorage (stored during registration)
      const storedUserId = localStorage.getItem('pending_verification_userId');
      if (storedUserId) {
        setUserId(storedUserId);
      }
    }

    // Set email for resend (unauthenticated users)
    if (!user?.email) {
      const storedEmail = localStorage.getItem('pending_verification_email');
      if (storedEmail) {
        setResendEmail(storedEmail);
      }
    }

    // Handle redirect from backend (after clicking email link)
    if (success === 'true') {
      setVerificationState('verified');
      setMessage(decodeURIComponent(messageParam || 'Your email has been verified successfully!'));
      // Clear stored userId
      localStorage.removeItem('pending_verification_userId');
      // Redirect back to the protected action/page that requested verification.
      setTimeout(() => {
        navigate(getRedirectTarget());
      }, 3000);
      return;
    }

    if (errorParam) {
      setVerificationState('error');
      setError(decodeURIComponent(errorParam));
      return;
    }

    // Handle email link click (userId and code in URL)
    if (urlUserId && urlCode) {
      verifyEmailWithCode(urlUserId, urlCode);
    } 
    // Legacy support: token and email
    else if (token && email) {
      verifyEmailToken();
    } 
    // Check if user is authenticated and needs verification
    else if (isAuthenticated && user) {
      checkEmailVerificationStatus();
    } 
    // Show manual entry form
    else {
      setVerificationState('needs_verification');
    }
  }, [urlUserId, urlCode, success, errorParam, messageParam, token, email, isAuthenticated, user]);

  // Cooldown timer for resend button
  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Code expiry countdown
  useEffect(() => {
    if (codeExpirySeconds <= 0) return;
    const timer = setInterval(() => {
      setCodeExpirySeconds((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [codeExpirySeconds]);

  /**
   * Verify email with userId and code
   */
  const verifyEmailWithCode = async (userIdentifier, code) => {
    setLoading(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/auth/verify-email`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId: userIdentifier, code })
      });

      const result = await response.json();

      if (response.ok) {
        setVerificationState('verified');
        setMessage(result.message || 'Your email has been verified successfully!');

        // Clear pending verification data
        localStorage.removeItem('pending_verification_userId');
        localStorage.removeItem('pending_verification_email');

        // Update user data if authenticated
        if (updateUser) {
          updateUser({
            ...(result.data?.user || user || {}),
            emailVerified: true,
            email_verified: true,
          });
        }

        // Redirect back to the protected action/page that requested verification.
        setTimeout(() => {
          navigate(getRedirectTarget());
        }, 3000);
      } else {
        setVerificationState('error');
        setError(result.error || result.message || 'Email verification failed. The code may be expired or invalid.');
      }
    } catch (error) {
      setVerificationState('error');
      setError('Network error occurred during verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verify email token from URL (legacy support)
   */
  const verifyEmailToken = async () => {
    setLoading(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/auth/verify-email`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, email })
      });

      const result = await response.json();

      if (response.ok) {
        setVerificationState('verified');
        setMessage('Your email has been verified successfully!');
        
        // Update user data if authenticated
        if (isAuthenticated && updateUser) {
          updateUser({ ...user, emailVerified: true });
        }
        
        // Redirect back to the protected action/page that requested verification.
        setTimeout(() => {
          navigate(getRedirectTarget());
        }, 3000);
      } else {
        setVerificationState('error');
        setError(result.message || 'Email verification failed. The link may be expired or invalid.');
      }
    } catch (error) {
      setVerificationState('error');
      setError('Network error occurred during verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle manual code submission
   */
  const handleCodeSubmit = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    if (!userId) {
      setError('User ID is required. Please log in or use the verification link from your email.');
      return;
    }

    await verifyEmailWithCode(userId, verificationCode);
  };

  /**
   * Check current user's email verification status
   */
  const checkEmailVerificationStatus = async () => {
    if (user?.emailVerified || user?.email_verified) {
      setVerificationState('verified');
      setMessage('Your email is already verified.');
    } else {
      setVerificationState('needs_verification');
    }
  };

  /**
   * Resend verification email
   */
  const handleResendVerification = async (targetEmail = null) => {
    if (resendCooldown > 0) return;

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const emailToVerify = targetEmail || user?.email;
      
      if (!emailToVerify) {
        throw new Error('No email address found');
      }

      const response = await api.post('/auth/resend-verification', { email: emailToVerify });
      const result = response.data;

      if (result.success !== false) {
        setMessage(`Verification email sent to ${emailToVerify}. Please check your inbox and spam folder.`);
        setResendCooldown(60); // 60 second cooldown
        setCodeExpirySeconds(600); // Reset 10-minute countdown
        setVerificationCode(''); // Clear old code
        
        if (targetEmail && targetEmail !== user?.email) {
          setShowEmailChange(false);
          setNewEmail('');
        }
      } else {
        setError(result.message || 'Failed to send verification email.');
      }
    } catch (error) {
      setError(error.message || 'Network error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Change email address
   */
  const handleEmailChange = async () => {
    if (!newEmail.trim()) {
      setError('Please enter a valid email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Please enter a valid email format');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/change-email', { newEmail });
      const result = response.data;

      if (result.success !== false) {
        setMessage(`Email change initiated. Verification email sent to ${newEmail}.`);
        setNewEmail('');
        setShowEmailChange(false);
        setResendCooldown(60);
        
        // Update user data
        if (updateUser) {
          updateUser({ ...user, email: newEmail, emailVerified: false });
        }
      } else {
        setError(result.message || 'Failed to change email address.');
      }
    } catch (error) {
      setError('Network error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Render verification status content
   */
  const renderVerificationContent = () => {
    switch (verificationState) {
      case 'checking':
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Verifying your email...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we verify your email address.
            </Typography>
          </Box>
        );

      case 'verified':
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="success.main">
              Email Verified Successfully!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {message}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(getRedirectTarget())}
              size="large"
            >
              Continue
            </Button>
          </Box>
        );

      case 'error':
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="error.main">
              Verification Failed
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setVerificationState('needs_verification');
                  setError('');
                  setVerificationCode('');
                }}
                startIcon={<Refresh />}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const emailForResend = user?.email || resendEmail;
                  if (emailForResend) {
                    handleResendVerification(emailForResend);
                  }
                  setVerificationState('needs_verification');
                  setError('');
                  setVerificationCode('');
                }}
                startIcon={<Send />}
                disabled={loading || resendCooldown > 0}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
              </Button>
            </Box>
          </Box>
        );

      case 'needs_verification':
      default:
        return (
          <Box sx={{ py: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Email sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Verify Your Email Address
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {user?.email ? (
                  <>We've sent a verification email to <strong>{user.email}</strong></>
                ) : resendEmail ? (
                  <>Enter the verification code sent to <strong>{resendEmail}</strong></>
                ) : (
                  <>Enter your verification code below</>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check your inbox and click the verification link, or enter the 6-digit code below.
              </Typography>
            </Box>

            {message && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {message}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!userId && !user && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Could not find your account details. Please{' '}
                <Button component="a" href="/register" variant="text" size="small" sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}>register</Button>
                {' '}or{' '}
                <Button component="a" href="/login" variant="text" size="small" sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}>log in</Button>
                {' '}first.
              </Alert>
            )}

            {/* Manual Code Entry Form — 6 individual digit boxes */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, textAlign: 'center' }}>
                Enter your 6-digit verification code:
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, sm: 1.5 }, mb: 2 }}>
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    ref={(el) => { digitRefs.current[index] = el; }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={verificationCode[index] || ''}
                    disabled={loading}
                    style={{
                      width: 48, height: 56,
                      textAlign: 'center',
                      fontSize: '24px',
                      fontWeight: 700,
                      border: `2px solid ${verificationCode[index] ? '#43B581' : '#d0d0d0'}`,
                      borderRadius: 10,
                      outline: 'none',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      fontFamily: 'monospace',
                      background: loading ? '#f5f5f5' : '#fff',
                      caretColor: '#43B581',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#43B581';
                      e.target.style.boxShadow = '0 0 0 3px #43B58122';
                      e.target.select();
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = verificationCode[index] ? '#43B581' : '#d0d0d0';
                      e.target.style.boxShadow = 'none';
                    }}
                    onChange={(e) => {
                      const digit = e.target.value.replace(/\D/g, '').slice(-1);
                      if (!digit && !e.target.value) return;
                      const newCode = verificationCode.split('');
                      newCode[index] = digit;
                      const joined = newCode.join('').slice(0, 6);
                      setVerificationCode(joined);
                      setError('');
                      if (digit && index < 5) {
                        digitRefs.current[index + 1]?.focus();
                      }
                      // Auto-submit when all 6 digits entered
                      if (joined.length === 6 && index === 5) {
                        setTimeout(() => handleCodeSubmit(), 100);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace') {
                        e.preventDefault();
                        const newCode = verificationCode.split('');
                        if (newCode[index]) {
                          newCode[index] = '';
                          setVerificationCode(newCode.join(''));
                        } else if (index > 0) {
                          newCode[index - 1] = '';
                          setVerificationCode(newCode.join(''));
                          digitRefs.current[index - 1]?.focus();
                        }
                      }
                      if (e.key === 'ArrowLeft' && index > 0) digitRefs.current[index - 1]?.focus();
                      if (e.key === 'ArrowRight' && index < 5) digitRefs.current[index + 1]?.focus();
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                      if (pasted) {
                        setVerificationCode(pasted);
                        setError('');
                        const focusIndex = Math.min(pasted.length, 5);
                        digitRefs.current[focusIndex]?.focus();
                        if (pasted.length === 6) {
                          setTimeout(() => handleCodeSubmit(), 100);
                        }
                      }
                    }}
                  />
                ))}
              </Box>

              {/* Code expiry countdown */}
              {codeExpirySeconds > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                  Code expires in {Math.floor(codeExpirySeconds / 60)}:{String(codeExpirySeconds % 60).padStart(2, '0')}
                </Typography>
              )}
              {codeExpirySeconds === 0 && verificationState === 'needs_verification' && verificationCode.length === 0 && (
                <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                  Code may have expired. Request a new one below.
                </Typography>
              )}

              <Box sx={{ maxWidth: 400, mx: 'auto' }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleCodeSubmit}
                  disabled={loading || verificationCode.length !== 6 || (!user && !userId)}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Verifying...' : 'Verify Email'}
                </Button>
              </Box>
            </Box>

            {/* Resend verification section */}
            {(user || resendEmail) && (
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Didn't receive the email? Check your spam folder or:
                </Typography>

                <Button
                  variant="outlined"
                  onClick={() => handleResendVerification(user?.email || resendEmail)}
                  disabled={loading || resendCooldown > 0}
                  startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                  sx={{ mb: 2 }}
                >
                  {resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : loading
                      ? 'Sending...'
                      : 'Resend Verification Email'
                  }
                </Button>
              </Box>
            )}

            {/* Email entry for resend when no email is available */}
            {!user && !resendEmail && (
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Didn't receive the email? Enter your email to resend:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, maxWidth: 400, mx: 'auto' }}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    size="small"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => handleResendVerification(resendEmail)}
                    disabled={loading || resendCooldown > 0 || !resendEmail.trim()}
                    startIcon={<Send />}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {resendCooldown > 0 ? `${resendCooldown}s` : 'Send'}
                  </Button>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Change email section */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Need to use a different email address?
              </Typography>
              
              {!showEmailChange ? (
                <Button
                  variant="text"
                  onClick={() => setShowEmailChange(true)}
                  size="small"
                >
                  Change Email Address
                </Button>
              ) : (
                <Box sx={{ maxWidth: 400, mx: 'auto' }}>
                  <TextField
                    fullWidth
                    label="New Email Address"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    error={!!error && error.includes('email')}
                    helperText={error && error.includes('email') ? error : 'Enter your new email address'}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowEmailChange(false);
                        setNewEmail('');
                        setError('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleEmailChange}
                      disabled={loading || !newEmail.trim()}
                    >
                      {loading ? 'Changing...' : 'Change Email'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Skip verification (for development) */}
            {process.env.NODE_ENV === 'development' && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="text"
                    color="secondary"
                    size="small"
                    onClick={() => navigate('/dashboard')}
                  >
                    Skip Verification (Development Only)
                  </Button>
                </Box>
              </>
            )}
          </Box>
        );
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        p: 2
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 500,
          width: '100%',
          p: 4,
          borderRadius: 2
        }}
      >
        {renderVerificationContent()}
      </Paper>
    </Box>
  );
};

export default EmailVerificationFlow;
